<script setup>
import { computed } from 'vue'
import { gradesToLabels } from '@/util/gradesort'
const props = defineProps({
  grades: {
    type: Array,
    required: true
  },
  level: {
    type: String,
    default: ''
  },
  levelLabel: {
    type: String,
    default: ''
  },
  superPage: {
    type: Boolean,
    default: false
  },
  variant: {
    type: String,
    default: 'default' // 'default' or 'aligned-goals'
  }
})

const levels = ['grade_level', 'approaching', 'developing']

const showLevel = computed(() => {
  return props.level !== ''
})

const gradeLabel = computed(() => {
  const grades = gradesToLabels(props.grades)
  let label = `Grade ${
    (grades.length > 1 && grades[0] + '-' + grades[grades.length - 1]) ||
    grades[0]
  }`

  if (grades[0] === 'Birth-3' || grades[0] === 'Pre-K' || grades[0] === 'Adult Transition') {
    label = label.replace('Grade ', '')
  }
  return label
})

const gradeContainsAdultTransition = computed(() => {
  return props.grades.length > 1 && props.grades.includes('Adult-Transition')
})

const containerClasses = computed(() => {
  const classes = ['flex', 'gap-2']
  
  // Variant-specific styles
  if (props.variant === 'aligned-goals') {
    classes.push('pt-1', 'text-blue-400')
  } else {
    classes.push('pt-4', 'pb-4', 'mb-[-6px]', 'text-blue-500')
  }
  
  // Adult transition alignment
  if (gradeContainsAdultTransition.value) {
    classes.push('items-start')
  } else {
    classes.push('items-center')
  }
  
  return classes
})

const lineBorderClass = computed(() => {
  return props.variant === 'aligned-goals' ? 'border-blue-400' : 'border-primary-500'
})

const bubblesGapClass = computed(() => {
  return props.variant === 'aligned-goals' ? 'gap-2.5' : 'gap-3.5'
})

const getBubbleClasses = (level) => {
  const classes = ['bubble', 'rounded-[50%]', 'border-x', 'border-y']
  
  // Size and border color
  if (props.variant === 'aligned-goals') {
    classes.push('h-3', 'w-3', 'border-blue-400')
  } else {
    classes.push('h-3.5', 'w-3.5', 'border-primary-500')
  }
  
  // Background color based on selection
  const isSelected = level === props.level
  if (isSelected) {
    classes.push(props.variant === 'aligned-goals' ? 'bg-blue-400' : 'bg-primary-500')
  } else {
    classes.push(props.variant === 'aligned-goals' ? 'bg-white' : 'bg-blue-100')
  }
  
  return classes
}
</script>
<template>
  <div :class="containerClasses">
    <div
      aria-hidden="true"
      class="relative flex items-center shrink-0"
      v-if="showLevel"
    >
      <div class="absolute inset-0 flex items-center" v-if="superPage">
        <div class="w-full border-t" :class="lineBorderClass"></div>
      </div>
      <div class="relative flex" :class="bubblesGapClass">
        <div
          v-if="superPage"
          :key="index"
          :class="getBubbleClasses(l)"
          v-for="(l, index) of levels"
        />
      </div>
    </div>
    <span class="text-[10px] font-bold shrink-0" v-if="showLevel">{{ levelLabel || level }}</span>
    <div v-if="variant !== 'aligned-goals'" class="text-right text-[10px] font-bold ml-auto" :class="{ 'shrink text-balance mt-[-4px]': gradeContainsAdultTransition }">{{ gradeLabel }}</div>
  </div>
</template>
